import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw } from "lucide-react";

interface UserStats {
  totalUsers: number;
  premiumUsers: number;
  eliteUsers: number;
  trialUsers: number;
  expiredUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  usersNearExpiration: number;
}

const UserAnalyticsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const subscriptionRef = useRef<any>(null);
  
  const [stats, setStats] = useState<UserStats>({
    totalUsers: 0,
    premiumUsers: 0,
    eliteUsers: 0,
    trialUsers: 0,
    expiredUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    usersNearExpiration: 0
  });

  const fetchUserStats = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Get total users count from users table
      const { count: totalUsersCount, error: totalUsersError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });
        
      if (totalUsersError) throw totalUsersError;
      
      // Get subscription data from subscription_status_view for plan analysis
      // Only query fields that actually exist in the view
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscription_status_view')
        .select('plan, subscription_status, has_access, ends_at, trial_active, grace_until');
        
      if (subscriptionError) throw subscriptionError;
      
      // Get users with access (active users) - those who have has_access = true
      const { count: activeUsersCount, error: activeUsersError } = await supabase
        .from('subscription_status_view')
        .select('*', { count: 'exact', head: true })
        .eq('has_access', true);
        
      if (activeUsersError) throw activeUsersError;
      
      // Users near expiration (within 7 days) - check ends_at and grace_until
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      const sevenDaysFromNowISO = sevenDaysFromNow.toISOString();
      const nowISO = new Date().toISOString();
      
      // Get users expiring within 7 days (check ends_at and grace_until)
      const { data: expiringUsersData, error: expiringUsersError } = await supabase
        .from('subscription_status_view')
        .select('ends_at, grace_until, trial_active, has_access')
        .eq('has_access', true) // Only count users who currently have access
        .or(`and(ends_at.lt.${sevenDaysFromNowISO},ends_at.gt.${nowISO}),and(grace_until.lt.${sevenDaysFromNowISO},grace_until.gt.${nowISO})`);
        
      if (expiringUsersError) throw expiringUsersError;
      
      // Calculate plan counts from subscription data
      const totalUsers = totalUsersCount || 0;
      let premiumUsers = 0;
      let eliteUsers = 0;
      let trialUsers = 0;
      let expiredUsers = 0;
      
      if (subscriptionData) {
        // Count users by plan and status
        premiumUsers = subscriptionData.filter(user => 
          user.plan === 'premium' && user.subscription_status === 'active'
        ).length;
        
        eliteUsers = subscriptionData.filter(user => 
          user.plan === 'elite' && user.subscription_status === 'active'
        ).length;
        
        trialUsers = subscriptionData.filter(user => 
          user.subscription_status === 'trial' && user.trial_active === true
        ).length;
        
        expiredUsers = subscriptionData.filter(user => 
          user.subscription_status === 'expired' || user.has_access === false
        ).length;
      }
      
      const activeUsers = activeUsersCount || 0;
      const inactiveUsers = totalUsers - activeUsers;
      const usersNearExpiration = expiringUsersData ? expiringUsersData.length : 0;
      
      setStats({
        totalUsers,
        premiumUsers,
        eliteUsers,
        trialUsers,
        expiredUsers,
        activeUsers,
        inactiveUsers,
        usersNearExpiration
      });
      
      setLastUpdated(new Date());
      
      
      
    } catch (error) {
      
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchUserStats();

    // Set up periodic refresh every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchUserStats(true);
    }, 30000);

    // Set up real-time subscription for subscription_status_view changes
    const setupRealtimeSubscription = async () => {
      try {
        subscriptionRef.current = supabase
          .channel('user-analytics-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'users'
            },
            (payload) => {
              
              // Debounce rapid changes by refreshing after a short delay
              setTimeout(() => {
                fetchUserStats(true);
              }, 1000);
            }
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'subscriptions'
            },
            (payload) => {
              
              // Debounce rapid changes by refreshing after a short delay
              setTimeout(() => {
                fetchUserStats(true);
              }, 1000);
            }
          )
          .subscribe();
      } catch (error) {
        
      }
    };

    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, []);

  const handleManualRefresh = () => {
    fetchUserStats(true);
  };

  const formatLastUpdated = () => {
    return lastUpdated.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Kullanıcı İstatistikleri</h2>
        <div className="flex items-center gap-4">
          <span className="text-xs text-muted-foreground">
            Son güncelleme: {formatLastUpdated()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Yenile
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Toplam Kullanıcılar</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.totalUsers}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Aktif Kullanıcılar</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.activeUsers}</p>
            <p className="text-xs text-muted-foreground">Erişimi olan kullanıcılar</p>
          </CardContent>
        </Card>
      </div>
      
      <h3 className="text-lg font-medium mt-6">Plan Dağılımı</h3>
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Premium</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.premiumUsers}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Elite</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.eliteUsers}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Deneme</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.trialUsers}</p>
          </CardContent>
        </Card>
      </div>
      
      <h3 className="text-lg font-medium mt-6">Özel Durumlar</h3>
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-yellow-500/20 bg-yellow-500/5">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Süresi Yakında Dolacak</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.usersNearExpiration}</p>
            <p className="text-xs text-muted-foreground">Önümüzdeki 7 gün içinde</p>
          </CardContent>
        </Card>
        
        <Card className="border-red-500/20 bg-red-500/5">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Süresi Dolmuş</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.expiredUsers}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserAnalyticsPanel; 